import { useFrame } from "@react-three/fiber";
import { useRef } from "react";
import type * as THREE from "three";

export interface PartyLightProps {
  baseRadius?: number;
  height?: number;
  speed?: number;
  phaseOffset?: number;
  baseIntensity?: number;
  /** Spotlight cone angle in radians (default: 0.4 ~23 degrees) */
  angle?: number;
  /** Soft edge falloff 0-1 (default: 0.5) */
  penumbra?: number;
}

function PartyLight({
  baseRadius = 5,
  height = 3,
  speed = 1,
  phaseOffset = 0,
  baseIntensity = 15,
  angle = 0.4,
  penumbra = 0.5,
}: PartyLightProps) {
  const lightRef = useRef<THREE.SpotLight>(null);
  const targetRef = useRef<THREE.Object3D>(null);

  useFrame(({ clock }) => {
    if (!lightRef.current || !targetRef.current) return;
    const t = clock.getElapsedTime() * speed;

    // Orbit around the scene
    const orbitAngle = t * 0.5 + phaseOffset * Math.PI * 2;
    const radiusVariation =
      baseRadius + Math.sin(t * 1.5 + phaseOffset * 5) * 2;
    lightRef.current.position.x = Math.cos(orbitAngle) * radiusVariation;
    lightRef.current.position.z = Math.sin(orbitAngle) * radiusVariation;

    // Vertical bobbing
    lightRef.current.position.y =
      height + Math.sin(t * 2 + phaseOffset * 3) * 1.5;

    // Move target slightly for beam sweep effect
    const targetSway = Math.sin(t * 0.8 + phaseOffset * 4) * 0.5;
    targetRef.current.position.set(targetSway, 0, targetSway * 0.5);

    // Cycle through colors
    const hue = (t * 0.15 + phaseOffset) % 1;
    lightRef.current.color.setHSL(hue, 1, 0.6);

    // Pulsing intensity
    const pulse = 0.8 + Math.sin(t * 4 + phaseOffset * 2) * 0.2;
    lightRef.current.intensity = baseIntensity * pulse;
  });

  return (
    <>
      {/* Target for spotlight to point at (center of scene) */}
      <object3D ref={targetRef} position={[0, 0, 0]} />
      <spotLight
        ref={lightRef}
        position={[0, height, 0]}
        intensity={baseIntensity}
        angle={angle}
        penumbra={penumbra}
        distance={30}
        decay={1.5}
        castShadow
        shadow-mapSize-width={512}
        shadow-mapSize-height={512}
        shadow-bias={-0.0001}
        target={targetRef.current ?? undefined}
      />
    </>
  );
}

export const PartyLights = () => {
  return (
    <>
      {/* Main spotlight - bright, wide beam */}
      <PartyLight
        baseRadius={8}
        height={6}
        speed={0.3}
        phaseOffset={0}
        baseIntensity={25}
        angle={0.35}
        penumbra={0.4}
      />
      {/* Secondary spotlight - tighter beam */}
      <PartyLight
        baseRadius={6}
        height={5}
        speed={0.4}
        phaseOffset={0.33}
        baseIntensity={20}
        angle={0.3}
        penumbra={0.5}
      />
      {/* Accent spotlight - high, sweeping */}
      <PartyLight
        baseRadius={9}
        height={7}
        speed={0.25}
        phaseOffset={0.66}
        baseIntensity={22}
        angle={0.4}
        penumbra={0.6}
      />
      {/* Close spotlight - dramatic */}
      <PartyLight
        baseRadius={5}
        height={4}
        speed={0.5}
        phaseOffset={0.5}
        baseIntensity={18}
        angle={0.25}
        penumbra={0.3}
      />
    </>
  );
};
